"use client";

import { Shield, Eye, Lock, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Servicedetail() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const services = [{
    number: "01",
    title: "AI THREAT DETECTION",
    description: "Leverage advanced machine learning algorithms to identify and neutralize cyber threats in real-time before they impact your business operations.",
    Icon: Shield
  }, {
    number: "02",
    title: "24/7 SECURITY MONITORING",
    description: "Continuous surveillance of your digital infrastructure by our expert team, ensuring rapid response to any suspicious activity around the clock.",
    Icon: Eye
  }, {
    number: "03",
    title: "PHISHING PREVENTION",
    description: "Protect your workforce from sophisticated social engineering attacks with our automated email filtering and employee awareness training programs.",
    Icon: Lock
  }];
  return <section data-section-id="4163" id="servicedetail" className="bg-slate-50 text-slate-900 relative overflow-hidden py-24 md:py-32 lg:py-40">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: "linear-gradient(var(--foreground) 1px, transparent 1px), linear-gradient(90deg, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "80px 80px"
    }} aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        {}
        <div ref={headerRef} className={`transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex items-center gap-3 mb-10 md:mb-14">
            <span className="font-default text-xs tracking-[0.3em] uppercase font-bold" style={{
            color: "var(--primary)"
          }}>
              01 — SOLUTIONS
            </span>
            <span className="h-px flex-1 max-w-[120px]" style={{
            backgroundColor: "var(--primary)"
          }} aria-hidden="true" />
          </div>

          <h2 className="font-default font-bold uppercase leading-[0.9] tracking-tight mb-10 md:mb-14" style={{
          fontSize: "clamp(3rem, 8vw, 9rem)"
        }}>
            <span className="block">CYBERSECURITY</span>
            <span className="block">
              FOR{" "}
              <span className="font-default italic font-light" style={{
              WebkitTextStroke: "2px var(--foreground)",
              WebkitTextFillColor: "transparent",
              color: "transparent"
            }}>
                MODERN
              </span>
            </span>
            <span className="block">ENTERPRISES.</span>
          </h2>

          <p className="font-default text-slate-700 leading-relaxed" style={{
          fontSize: "19px",
          maxWidth: "640px"
        }}>
            InfinitySecure provides comprehensive, AI-driven protection for your digital assets. We combine proactive threat intelligence with rapid incident response to ensure your business remains secure, compliant, and resilient against the evolving landscape of cyber threats.
          </p>
        </div>

        {}
        <div ref={gridRef} className="relative mt-20 md:mt-28">
          {}
          <div className="hidden lg:block absolute left-0 right-0 top-1/2 -translate-y-1/2 h-px pointer-events-none z-0" style={{
          background: "linear-gradient(90deg, transparent 0%, var(--primary) 15%, var(--primary) 85%, transparent 100%)",
          opacity: 0.4
        }} aria-hidden="true" />

          <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {services.map((service, i) => {
            const Icon = service.Icon;
            return <article data-index={i} key={service.number} className={`group relative flex flex-col lg:min-h-[400px] border shadow-lg transition-all duration-500 ease-out hover:-translate-y-2 hover:shadow-xl ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
              backgroundColor: "white",
              borderColor: "var(--border)",
              transitionDelay: gridVisible ? `${i * 120}ms` : "0ms"
            }} onMouseEnter={e => {
              e.currentTarget.style.borderColor = "var(--primary)";
            }} onMouseLeave={e => {
              e.currentTarget.style.borderColor = "var(--border)";
            }}>
                  {}
                  <div className="px-8 pt-8">
                    <span className="font-default text-5xl md:text-6xl font-light leading-none block" style={{
                  color: "var(--primary)"
                }}>
                      {service.number}
                    </span>
                  </div>

                  {}
                  <div className="px-8 pt-10 pb-6">
                    <Icon className="w-12 h-12 stroke-1" style={{
                  color: "var(--foreground)"
                }} strokeWidth={1} aria-hidden="true" />
                  </div>

                  {}
                  <div className="px-8 flex-1 flex flex-col">
                    <h3 className="font-default font-bold text-xl md:text-2xl tracking-tight uppercase mb-4 leading-tight">
                      {service.title}
                    </h3>
                    <p className="font-default text-sm md:text-base leading-relaxed text-slate-600 mb-8">
                      {service.description}
                    </p>
                  </div>

                  {}
                  <div className="px-8 pb-8 pt-4 border-t mt-auto" style={{
                borderColor: "var(--border)"
              }}>
                    <Link to="/ContactPage" className="inline-flex items-center gap-2 font-default text-xs tracking-[0.25em] uppercase font-bold transition-all duration-300 hover:gap-3" style={{
                  color: "var(--primary)"
                }}>
                      LEARN MORE
                      <ArrowRight className="w-4 h-4" strokeWidth={2} />
                    </Link>
                  </div>

                  {}
                  <span className="absolute top-0 right-0 w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{
                backgroundColor: "var(--primary)"
              }} aria-hidden="true" />
                </article>;
          })}
          </div>
        </div>

        {}
        <div className="mt-20 md:mt-28 flex flex-col md:flex-row items-start md:items-center justify-between gap-8 border-t pt-12" style={{
        borderColor: "var(--border)"
      }}>
          <div className="max-w-xl">
            <p className="font-default text-xs tracking-[0.3em] uppercase mb-3 font-bold" style={{
            color: "var(--primary)"
          }}>
              SECURITY ASSESSMENT
            </p>
            <p className="font-default text-2xl md:text-3xl font-bold tracking-tight leading-tight">
              Ready to secure your business infrastructure?
            </p>
          </div>
          <Button asChild size="lg" className="font-default text-xs tracking-[0.25em] uppercase font-bold h-14 px-8 bg-slate-900 text-white hover:bg-slate-800">
            <Link to="/ContactPage">
              REQUEST ASSESSMENT
              <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}